import { TZDate, TZDateMini } from '@date-fns/tz';
import { addDays, addMonths, addYears } from 'date-fns';
import { type Dispatch } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { validateCalendarSelection } from './calendar-validation.js';
import type { TimeframeV2 } from '../../../core/types/time.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../../forms-core/state/formfield-messages-state-reducer.js';
import { _getTimeValueFromSimpleValue } from '../../forms-core/time/helpers.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type {
  CalendarReducerAction,
  CalendarState,
} from '../types/shared-types.js';
import {
  getDateFromSelection,
  getPosition,
  getWeekStartOrEnd,
} from '../utils/helpers.js';

/** Calendar reducer */
export function reducer(
  state: CalendarState,
  action: CalendarReducerAction,
  handleValidation: Dispatch<FormFieldMessagesReducerAction>,
  onChange?: (date: TimeframeV2 | string | null) => void,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
): CalendarState {
  const configuredTimezone = getTimezone();

  switch (action.type) {
    case 'activeDateChange':
      return {
        ...state,
        activeDate: action.payload.activeDate,
        currentView: action.payload.changeToView ?? state.currentView,
      };
    case 'typeChanged':
      return { ...state, ...action.payload };
    case 'currentViewChange':
      return { ...state, currentView: action.payload };
    case 'isCalendarBodyFocusedChange':
      return { ...state, isCalendarBodyFocused: action.payload };
    case 'weekStartsOnChange':
      return { ...state, weekStartsOn: action.payload };
    case 'selectionChanged': {
      if (!action.payload) {
        requestAnimationFrame(() => onChange?.(null));
        return state;
      }

      const newTimeValue = _getTimeValueFromSimpleValue(
        action.payload,
        state.timeOffset,
        state.position,
        undefined,
        state.min?.toISOString(),
        state.max?.toISOString(),
        precision,
      );

      const newSelectedDate = getDateFromSelection(
        state.selectedDate,
        state.type,
        newTimeValue,
        state.position,
        state.min?.toISOString(),
        state.max?.toISOString(),
        precision,
      );

      // The onChange should be triggered after updating the state to prevent a bad setState() call during rendering.
      requestAnimationFrame(() => {
        if (!newSelectedDate) {
          return onChange?.(null);
        }

        if (
          isTimeframeV2(newSelectedDate, state.type) &&
          'from' in newSelectedDate &&
          'to' in newSelectedDate
        ) {
          // @ts-expect-error TimeValue types are wrong here as they should not handle undefined, but rather null.
          newSelectedDate.to ??= null;
          // @ts-expect-error TimeValue types are wrong here as they should not handle undefined, but rather null.
          newSelectedDate.from ??= null;

          return onChange?.(newSelectedDate as TimeframeV2);
        }

        if ('value' in newSelectedDate) {
          onChange?.(newSelectedDate.value);
        }
      });

      return state;
    }
    case 'navigateCurrentView': {
      let newActiveDate: TZDate;
      const { amount, unit } = action.payload;
      const affectedScale = unit ?? state.currentView;
      const realAmount =
        typeof amount === 'number'
          ? amount
          : getWeekStartOrEnd(amount, state.activeDate, state.weekStartsOn);

      const configuredTimezone = getTimezone();
      const activeDate = new TZDateMini(state.activeDate, configuredTimezone);

      switch (affectedScale) {
        case 'day':
          newActiveDate = addDays(activeDate, realAmount);
          break;
        case 'month':
          newActiveDate = addMonths(activeDate, realAmount);
          break;
        case 'year':
          newActiveDate = addYears(activeDate, realAmount);
          break;
      }

      return {
        ...state,
        activeDate: newActiveDate,
      };
    }
    case 'hoveredItemChanged': {
      return { ...state, hoveredDate: action.payload };
    }
    case 'valueChanged': {
      const updatedValue = action.payload;

      const internalError = validateCalendarSelection(
        updatedValue,
        state,
        handleValidation,
      );

      handleValidation({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });

      const activeDate = isTimeframeV2(updatedValue, state.type)
        ? updatedValue.to?.absoluteDate || updatedValue.from?.absoluteDate
        : updatedValue?.absoluteDate;

      return {
        ...state,
        selectedDate: updatedValue,
        activeDate: new TZDateMini(
          activeDate ?? state.today.toISOString(),
          configuredTimezone,
        ),
        hoveredDate: null,
        position: getPosition(state.position, updatedValue),
        errorId: internalError,
      };
    }
    default:
      // Should never happen
      throw new Error('The dispatched action does not exist.');
  }
}
